<template>
  <div class="workspace-phases-tab">
    <!-- Search Toolbar -->
    <div v-if="showSearch" class="workspace-toolbar">
      <div class="search-box">
        <span class="search-icon">🔍</span>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('workspace.search')"
          class="search-input"
        />
      </div>
    </div>

    <!-- Phases Data Grid -->
    <div class="data-grid">
      <AppDataGrid
        :row-data="phaseGridData"
        :column-defs="phaseColumnDefs"
        :actions="phaseActions"
        :pagination="true"
        :page-size="10"
        :page-size-options="itemsPerPageOptions"
        :suppress-pagination-panel="false"
        :show-search="false"
        grid-class="phases-grid"
        grid-style="width: 100%;"
        dom-layout="autoHeight"
        :default-col-def="{ wrapText: false, autoHeight: false }"
        @row-clicked="handlePhaseRowClick"
        @action-clicked="handlePhaseAction"
      />
    </div>

    <!-- Phase Form Modal -->
    <PhaseFormModal
      :show="showPhaseModal"
      :phase="editingPhase"
      :project-id="projectId"
      @close="closePhaseModal"
      @saved="handlePhaseSaved"
    />

    <!-- Delete Confirmation Modal - Phase -->
    <ConfirmModal
      v-if="showDeleteConfirm"
      :isVisible="showDeleteConfirm"
      :title="$t('phases.deletePhase')"
      :message="$t('workspace.confirmDelete.phase')"
      :details="deletePhaseName"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      type="danger"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useNotifications } from '@/composables/useNotifications'
import { usePermissions } from '@/composables/usePermissions'
import AppDataGrid from '@/components/base/AppDataGrid.vue'
import PhaseFormModal from '../modals/PhaseFormModal.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import projectPhasesService from '@/services/projectPhases.service'
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils'
import { getAvatarColor, getInitials, getProgressClass } from '@/utils/uiHelpers'

// Interfaces
interface Phase {
  id?: number
  projectId: number
  name: string
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
  actualStartDate?: string
  actualEndDate?: string
  dependencyPhaseId?: number
  dependencyPhaseName?: string
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'
  isDelivered?: boolean
  deliveredDate?: string
  createdBy?: number
  createdAt?: string
  updatedAt?: string
}

interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: string
  email: string
  phone?: string
  address?: string
  createdAt?: string
  updatedAt?: string
}

interface Task {
  id?: number
  projectId?: number
  name: string
  description?: string
  amount: number
  startDate?: string
  endDate?: string
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD'
  assignedTo?: string
  createdBy?: number
  updatedBy?: number
  createdAt?: string
  updatedAt?: string
  intervenantName?: string
  intervenantId?: number
  phaseId?: number
  phaseName?: string
}

const { t } = useI18n()
const router = useRouter()
const { notifySuccess, notifyError } = useNotifications()

const props = defineProps<{
  projectId: number
  phases: Phase[]
  tasks: Task[]
  allIntervenants: Intervenant[]
  // Helper functions passed from parent
  getPhaseProgress: (phaseId: number) => number
  getPhaseBudget: (phaseId: number) => number
  getPhaseIntervenants: (phaseId: number) => Intervenant[]
  // Configuration options
  showSearch?: boolean
}>()

const emit = defineEmits<{
  phaseSelected: [phase: Phase]
  phaseCreated: []
  phaseUpdated: []
  phaseDeleted: []
}>()

// Provide default values for optional props
const showSearch = computed(() => props.showSearch !== false) // Default to true

// State
const selectedPhase = ref<Phase | null>(null)
const searchQuery = ref('')
const showPhaseModal = ref(false)
const editingPhase = ref<Phase | null>(null)
const showDeleteConfirm = ref(false)
const deletePhaseId = ref<number | null>(null)
const deletePhaseName = ref('')

// Pagination configuration for AppDataGrid
const itemsPerPageOptions = [5, 10, 25, 50, 100]

// Reactive formatted amounts
const formattedAmounts = ref<Record<string, string>>({})

// Computed - Apply search filter
const searchFilteredPhases = computed(() => {
  let result = [...props.phases]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((phase: Phase) =>
      phase.name?.toLowerCase().includes(query) ||
      phase.description?.toLowerCase().includes(query)
    )
  }

  return result
})

// Methods
const selectPhase = (phase: Phase) => {
  selectedPhase.value = phase
  emit('phaseSelected', phase)
}

const editPhase = (phase: Phase) => {
  editingPhase.value = { ...phase }
  showPhaseModal.value = true
}

const configurePhase = (phase: Phase) => {
  router.push({
    name: 'PhaseConfigurer',
    params: {
      projectId: props.projectId.toString(),
      phaseId: phase.id!.toString()
    }
  })
}

const openPhaseWorkspace = (phase: Phase) => {
  router.push({
    name: 'PhaseWorkspace',
    params: {
      projectId: props.projectId.toString(),
      phaseId: phase.id!.toString()
    }
  })
}

const deletePhase = (id: number) => {
  const phase = searchFilteredPhases.value.find((p: Phase) => p.id === id)
  deletePhaseId.value = id
  deletePhaseName.value = phase?.name || ''
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  if (deletePhaseId.value === null) return
  
  try {
    await projectPhasesService.deletePhase(
      props.projectId.toString(),
      deletePhaseId.value.toString()
    )
    notifySuccess(t('workspace.success.phaseDeleted'))
    emit('phaseDeleted')
  } catch (error) {
    notifyError(t('workspace.errors.phaseDeleteFailed'))
  } finally {
    cancelDelete()
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
  deletePhaseId.value = null
  deletePhaseName.value = ''
}

const closePhaseModal = () => {
  showPhaseModal.value = false
  editingPhase.value = null
}

const handlePhaseSaved = () => {
  closePhaseModal()
  if (editingPhase.value?.id) {
    emit('phaseUpdated')
  } else {
    emit('phaseCreated')
  }
}

// Helper method to get task count for a phase
const getPhaseTaskCount = (phaseId: number): number => {
  return props.tasks.filter((t: Task) => t.phaseId === phaseId).length
}

// Grid Configuration
const { hasPermissionForProject, PermissionType } = usePermissions()

// Helper functions for cell rendering
const getStatusDotColor = (status: string): string => {
  switch (status) {
    case 'completed': return '#10b981'
    case 'in_progress': return '#3b82f6'
    case 'on_hold': return '#f59e0b'
    case 'cancelled': return '#ef4444'
    case 'not_started': return '#9ca3af'
    default: return '#9ca3af'
  }
}

const getStatusBadgeColors = (status: string) => {
  switch (status) {
    case 'completed': return { bg: '#d1fae5', text: '#065f46' }
    case 'in_progress': return { bg: '#dbeafe', text: '#1e40af' }
    case 'on_hold': return { bg: '#fed7aa', text: '#92400e' }
    case 'cancelled': return { bg: '#fee2e2', text: '#991b1b' }
    case 'not_started': return { bg: '#f3f4f6', text: '#374151' }
    default: return { bg: '#f3f4f6', text: '#374151' }
  }
}

// Custom cell renderers for phases grid
const phaseCellRenderers: Record<string, (params: any) => string> = {
  name: (params: any) => {
    const status = params.data.statusRaw?.toLowerCase() || 'not_started'
    return `
      <div style="display: flex; align-items: center; gap: 12px;">
        <span style="
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${getStatusDotColor(status)};
          flex-shrink: 0;
        "></span>
        <strong style="font-weight: 600; color: #1f2937;">${params.value}</strong>
      </div>
    `
  },
  
  status: (params: any) => {
    if (!params.value) return ''
    const status = params.data.statusRaw?.toLowerCase() || 'not_started'
    const colors = getStatusBadgeColors(status)
    return `
      <span style="
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 6px 14px;
        border-radius: 20px;
        font-weight: 600;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        background-color: ${colors.bg};
        color: ${colors.text};
        white-space: nowrap;
      ">
        <span style="width: 6px; height: 6px; border-radius: 50%; background-color: currentColor;"></span>
        ${params.value}
      </span>
    `
  },
  
  progress: (params: any) => {
    const progress = params.data.progressValue || 0
    const progressClass = getProgressClass(progress)
    let barColor = '#9ca3af' // default gray
    
    if (progressClass.includes('success')) barColor = '#10b981'
    else if (progressClass.includes('warning')) barColor = '#f59e0b'
    else if (progressClass.includes('danger')) barColor = '#ef4444'
    else if (progressClass.includes('info')) barColor = '#3b82f6'
    
    return `
      <div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">
        <div style="
          position: relative;
          min-width: 100px;
          height: 10px;
          background-color: #f3f4f6;
          border-radius: 4px;
          overflow: hidden;
        ">
          <div style="
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            width: ${progress}%;
            background-color: ${barColor};
            transition: width 0.3s ease;
          "></div>
        </div>
        <small style="font-size: 0.75rem; color: #6b7280; font-weight: 500;">${progress}%</small>
      </div>
    `
  },
  
  budget: (params: any) => {
    if (!params.value) return '-'
    return `
      <span style="
        font-weight: 600;
        color: #059669;
        font-size: 15px;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      ">${params.value}</span>
    `
  },
  
  tasks: (params: any) => {
    const taskCount = params.data.taskCount || 0
    const progress = params.data.progressValue || 0
    let icon = '⏸'
    let iconColor = '#9ca3af'
    
    if (progress === 100) {
      icon = '✓'
      iconColor = '#10b981'
    } else if (progress > 0) {
      icon = '⏳'
      iconColor = '#3b82f6'
    }
    
    return `
      <div style="
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        background-color: #f9fafb;
        border-radius: 8px;
        font-size: 14px;
      ">
        <span style="font-weight: 500; color: #374151;">${taskCount} ${t('workspace.phases.tasksLabel')}</span>
        <span style="font-size: 16px; color: ${iconColor};">${icon}</span>
      </div>
    `
  },
  
  intervenants: (params: any) => {
    const intervenants = params.data.intervenantsData || []
    const visibleCount = Math.min(3, intervenants.length)
    const remainingCount = Math.max(0, intervenants.length - 3)
    
    if (intervenants.length === 0) {
      return '<span style="color: #9ca3af;">-</span>'
    }
    
    let html = '<div style="display: flex; align-items: center;">'
    
    // Show up to 3 avatars
    for (let i = 0; i < visibleCount; i++) {
      const intervenant = intervenants[i]
      const bgColor = getAvatarColor(intervenant.name || '')
      const initials = getInitials(intervenant.name || '')
      const marginLeft = i > 0 ? '-8px' : '0'
      
      html += `
        <div style="
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: ${bgColor};
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          margin-left: ${marginLeft};
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        " title="${intervenant.name}">
          ${initials}
        </div>
      `
    }
    
    // Show +X indicator if more than 3
    if (remainingCount > 0) {
      html += `
        <div style="
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: #e5e7eb;
          color: #374151;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          margin-left: -8px;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        ">
          +${remainingCount}
        </div>
      `
    }
    
    html += '</div>'
    return html
  }
}

// Prepare phase data for grid
const phaseGridData = computed(() => {
  return searchFilteredPhases.value.map((phase: Phase) => ({
    id: phase.id,
    name: phase.name,
    status: t(`phases.statusOptions.${phase.status}`),
    statusRaw: phase.status,
    progress: `${props.getPhaseProgress(phase.id!)}%`,
    progressValue: props.getPhaseProgress(phase.id!),
    budget: getFormattedAmount(`phase-${phase.id}`),
    tasks: `${getPhaseTaskCount(phase.id!)}`,
    taskCount: getPhaseTaskCount(phase.id!),
    intervenants: props.getPhaseIntervenants(phase.id!).length.toString(),
    intervenantsData: props.getPhaseIntervenants(phase.id!),
    _originalData: phase
  }))
})

// Column definitions for phases grid
const phaseColumnDefs = computed(() => [
  {
    field: 'name',
    headerName: t('workspace.phases.name'),
    cellRenderer: phaseCellRenderers.name,
    minWidth: 200,
    flex: 1,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'status',
    headerName: t('workspace.phases.status'),
    cellRenderer: phaseCellRenderers.status,
    width: 200,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'progress',
    headerName: t('workspace.phases.progress'),
    cellRenderer: phaseCellRenderers.progress,
    width: 150,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'budget',
    headerName: t('workspace.phases.budget'),
    cellRenderer: phaseCellRenderers.budget,
    width: 140,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'tasks',
    headerName: t('workspace.phases.tasks'),
    cellRenderer: phaseCellRenderers.tasks,
    width: 160,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'intervenants',
    headerName: t('workspace.phases.intervenants'),
    cellRenderer: phaseCellRenderers.intervenants,
    width: 180,
    sortable: false,
    wrapText: false,
    autoHeight: false
  }
])

// Action definitions for phases grid
const phaseActions = computed(() => {
  const actions = []
  
  // View Details button - now selects the phase
  if (hasPermissionForProject(PermissionType.ACCESS_PHASES, props.projectId).value) {
    actions.push({
      name: 'viewDetails',
      label: t('workspace.actions.viewDetails'),
      icon: 'fas fa-chart-bar',
      class: 'view-btn',
      handler: (data: any) => selectPhase(data._originalData)
    })
  }
  
  // Edit button
  if (hasPermissionForProject(PermissionType.UPDATE_PHASE, props.projectId).value) {
    actions.push({
      name: 'edit',
      label: t('common.edit'),
      icon: 'fas fa-edit',
      class: 'edit-btn',
      handler: (data: any) => editPhase(data._originalData)
    })
  }
  
  // Configure button
  if (hasPermissionForProject(PermissionType.UPDATE_PHASE, props.projectId).value) {
    actions.push({
      name: 'configure',
      label: t('workspace.actions.configure'),
      icon: 'fas fa-cog',
      class: 'edit-btn',
      handler: (data: any) => configurePhase(data._originalData)
    })
  }
  
  // Delete button
  if (hasPermissionForProject(PermissionType.DELETE_PHASE, props.projectId).value) {
    actions.push({
      name: 'delete',
      label: t('common.delete'),
      icon: 'fas fa-trash',
      class: 'delete-btn',
      handler: (data: any) => deletePhase(data.id)
    })
  }
  
  return actions
})

// Handle phase grid actions
const handlePhaseAction = (action: any, data: any) => {
  action.handler(data)
}

// Handle phase row clicks - now opens phase workspace
const handlePhaseRowClick = (event: any) => {
  openPhaseWorkspace(event.data._originalData)
}

const formatCurrency = async (amount: number, key?: string) => {
  if (amount === null || amount === undefined) return '-'
  try {
    const formatted = await formatCurrencyWithUserPreference(amount)
    if (key) {
      formattedAmounts.value[key] = formatted
    }
    return formatted
  } catch (err) {
    console.warn('Error formatting currency:', err)
    return amount.toString()
  }
}

const getFormattedAmount = (key: string) => {
  return formattedAmounts.value[key] || '...'
}

// Format amounts when phases change
const formatAllAmounts = async () => {
  try {
    for (const phase of searchFilteredPhases.value) {
      if (phase.id) {
        const key = `phase-${phase.id}`
        const budget = props.getPhaseBudget(phase.id)
        await formatCurrency(budget, key)
      }
    }
  } catch (error) {
    console.warn('Error formatting amounts:', error)
  }
}

// Watch for prop changes to reformat amounts
watch(() => props.phases, formatAllAmounts, { immediate: true, deep: true })
watch(() => props.tasks, formatAllAmounts, { deep: true })
</script>

<style scoped>
.workspace-phases-tab {
  padding: 0;
}

/* Search Toolbar */
.workspace-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-500);
  font-size: 0.875rem;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

/* Data Grid */
.data-grid {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .search-box {
    max-width: 100%;
  }
}
</style>
